import React from 'react';
import { ScrollView, StyleSheet, View } from 'react-native';

import { theme } from '@garnish/constants';
import { AnimatedViews, useResponsive, VStack } from '@garnish/core';

import { Layout } from '../Layout';
import { OrderStatusDivider } from '../OrderStatusDivider';

// ─────────────────────────────────────────────────────────────────────────────

export const OrderStatusLoading = () => {
  const { currentBreakpoint } = useResponsive();

  if (currentBreakpoint.isXS) {
    return (
      <ScrollView
        scrollEnabled={false}
        style={styles.container}
        contentContainerStyle={styles.scrollContentXS}
      >
        <Layout.Container>
          <View style={styles.infoXS}>
            <OrderInfoPlaceholder />
          </View>

          <Layout.Map>
            <AnimatedViews.Pulse index={1} style={styles.map} />
          </Layout.Map>

          <Layout.Content>
            <OrderDetailsPlaceholder />
          </Layout.Content>
        </Layout.Container>
      </ScrollView>
    );
  }

  return (
    <View style={styles.container}>
      <Layout.Container>
        <Layout.Map>
          <AnimatedViews.Pulse style={styles.map} />
        </Layout.Map>

        <Layout.SidePanel>
          <Layout.Info>
            <OrderInfoPlaceholder />
          </Layout.Info>

          <Layout.Content>
            <OrderDetailsPlaceholder />
          </Layout.Content>
        </Layout.SidePanel>
      </Layout.Container>
    </View>
  );
};

// ─── Components ──────────────────────────────────────────────────────────────

const OrderInfoPlaceholder = () => {
  return (
    <AnimatedViews.Pulse>
      <View style={styles.infoHeader}>
        <View style={styles.title} />
        <View style={styles.orderNumber} />
      </View>

      <View style={styles.details}>
        <View style={styles.detailsLine} />
        <View style={styles.detailsLineShort} />
      </View>

      <View style={styles.tracker}>
        <View style={styles.trackerLine} />

        <View style={styles.trackerNode}>
          <View style={styles.trackerDot} />
          <View style={styles.trackerLabel} />
        </View>

        <View style={styles.trackerNode}>
          <View style={styles.trackerDot} />
          <View style={styles.trackerLabel} />
        </View>

        <View style={styles.trackerNode}>
          <View style={styles.trackerDot} />
          <View style={styles.trackerLabel} />
        </View>

        <View style={styles.trackerNode}>
          <View style={styles.trackerDot} />
          <View style={styles.trackerLabel} />
        </View>
      </View>
    </AnimatedViews.Pulse>
  );
};

const OrderDetailsPlaceholder = () => {
  return (
    <VStack gap={0} divider={<OrderStatusDivider />}>
      <AnimatedViews.Pulse index={2} style={styles.section}>
        <View style={styles.sectionHeading} />
        <View style={styles.addressLineShort} />
        <View style={styles.addressLine} />
        <View style={styles.addressLineLong} />
      </AnimatedViews.Pulse>

      <AnimatedViews.Pulse index={3} style={styles.section}>
        <View style={styles.lineItem}>
          <View style={styles.lineItemImage} />

          <View style={styles.lineItemDetails}>
            <View style={styles.lineItemName} />
            <View style={styles.lineItemDescription} />
          </View>

          <View style={styles.lineItemPrice} />
        </View>
      </AnimatedViews.Pulse>

      <AnimatedViews.Pulse index={4} style={styles.section}>
        <View style={styles.ledgerRow}>
          <View style={styles.ledgerLabel} />
          <View style={styles.ledgerValue} />
        </View>

        <View style={styles.ledgerRow}>
          <View style={styles.ledgerLabelShort} />
          <View style={styles.ledgerValue} />
        </View>

        <View style={styles.ledgerRow}>
          <View style={styles.ledgerLabel} />
          <View style={styles.ledgerValue} />
        </View>
      </AnimatedViews.Pulse>
    </VStack>
  );
};

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flex: 1,
    overflow: 'hidden',
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  scrollContentXS: {
    flexGrow: 1,
  },
  infoXS: {
    flexShrink: 0,
    paddingTop: theme.spacing['2'],
    paddingHorizontal: theme.spacing['4'],
    paddingBottom: theme.spacing['10'],
  },
  map: {
    flex: 1,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.ALMOST_TRANSPARENT,
  },
  infoHeader: {
    rowGap: theme.spacing['4'],
  },
  title: {
    width: '72%',
    height: 32,
    marginVertical: 4,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  orderNumber: {
    width: 104,
    height: 14,
    marginVertical: 3,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  details: {
    rowGap: 6,
    marginTop: theme.spacing['4'],
  },
  detailsLine: {
    width: '88%',
    height: 14,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  detailsLineShort: {
    width: '56%',
    height: 14,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  tracker: {
    flexDirection: 'row',
    marginTop: theme.spacing['8'],
  },
  trackerLine: {
    position: 'absolute',
    top: 5,
    right: 0,
    left: 0,
    height: 2,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  trackerNode: {
    flex: 1,
    alignItems: 'center',
    rowGap: theme.spacing['3'],
  },
  trackerDot: {
    width: 12,
    height: 12,
    borderRadius: theme.radius['3xLarge'],
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  trackerLabel: {
    width: '64%',
    height: 10,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  section: {
    rowGap: theme.spacing['2'],
    paddingVertical: theme.spacing['6'],
  },
  sectionHeading: {
    width: 140,
    height: 16,
    marginBottom: theme.spacing['3'],
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  addressLineShort: {
    width: 56,
    height: 12,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  addressLine: {
    width: 96,
    height: 12,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  addressLineLong: {
    width: 136,
    height: 12,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  lineItem: {
    flexDirection: 'row',
    alignItems: 'center',
    columnGap: theme.spacing['4'],
  },
  lineItemImage: {
    width: 88,
    height: 88,
    borderRadius: theme.radius.large,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  lineItemDetails: {
    flex: 1,
    rowGap: theme.spacing['2'],
  },
  lineItemName: {
    width: '72%',
    height: 14,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  lineItemDescription: {
    width: '48%',
    height: 12,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  lineItemPrice: {
    width: 44,
    height: 14,
    alignSelf: 'flex-start',
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  ledgerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: theme.spacing['1'],
  },
  ledgerLabel: {
    width: 72,
    height: 14,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  ledgerLabelShort: {
    width: 40,
    height: 14,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
  ledgerValue: {
    width: 48,
    height: 14,
    borderRadius: theme.radius.small,
    backgroundColor: theme.colors.OPACITY.DARK_KALE.LIGHTEST,
  },
});
